import { useState, useEffect, useMemo } from "react";
import ProductCard from "./components/ProductCard";
import { fetchProductsFromGoogleSheet } from "../../services/googleSheets";

const categories = [
  { id: "all", label: "Tous" },
  { id: "Coques", label: "Coques" },
  { id: "Protections écran", label: "Protections écran" },
  { id: "Chargeurs", label: "Chargeurs" },
  { id: "Câbles", label: "Câbles" },
  { id: "Supports voiture", label: "Supports voiture" },
  { id: "Accessoires utiles", label: "Accessoires utiles" },
];

interface Product {
  id: string;
  status: string;
  category: string;
  subcategory: string;
  brand: string;
  title: string;
  description: string;
  price: string;
  main_image: string;
  images: string;
  affiliate_link: string;
  badge: string;
  created_at: string;
}

export default function Accessoires() {
  const [products, setProducts] = useState<Product[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeBrand, setActiveBrand] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProducts() {
      try {
        setLoading(true);
        const data = await fetchProductsFromGoogleSheet();
        const validProducts = data
          .filter(
            (p: Product) =>
              p.status?.toLowerCase() === "published" ||
              p.status?.toLowerCase() === "active"
          )
          .sort((a: Product, b: Product) => {
            // Keep the Ultra Fine iPhone family in ascending model order.
            const model = (p: Product) =>
              p.id.startsWith("coquesdeluxe-ultrafine-iphone")
                ? Number(p.id.replace("coquesdeluxe-ultrafine-iphone", ""))
                : null;
            const ma = model(a);
            const mb = model(b);
            if (ma !== null && mb !== null && Number.isFinite(ma) && Number.isFinite(mb)) {
              return ma - mb;
            }
            const da = a.created_at ? new Date(a.created_at).getTime() : 0;
            const db = b.created_at ? new Date(b.created_at).getTime() : 0;
            return db - da;
          });
        setProducts(validProducts);
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }
    loadProducts();

    function handleVisibilityChange() {
      if (!document.hidden) {
        loadProducts();
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const interval = setInterval(() => {
      loadProducts();
    }, 60000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  // Extract unique brands from products, sorted alphabetically
  const availableBrands = useMemo(() => {
    const brands = new Set<string>();
    products.forEach((p) => {
      if (p.brand?.trim()) brands.add(p.brand.trim());
    });
    return Array.from(brands).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const categoryMatch = activeCategory === "all" || p.category === activeCategory;
      const brandMatch = activeBrand === "all" || p.brand === activeBrand;
      return categoryMatch && brandMatch;
    });
  }, [products, activeCategory, activeBrand]);

  // Reset brand when category changes (optional UX — keeps it simpler)
  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    setActiveBrand("all");
  };

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative bg-oya-black pt-28 pb-12 md:pt-36 md:pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="inline-block px-4 py-1.5 bg-oya-red/20 rounded-full mb-6">
            <span className="text-oya-red-light text-sm font-medium">/ ACCESSOIRES</span>
          </div>
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-white max-w-3xl leading-tight">
            Accessoires recommandés par OYA PHONE
          </h1>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
            Une sélection d'accessoires utiles pour protéger, charger et améliorer votre smartphone au quotidien.
          </p>
        </div>
      </section>

      {/* Filters — Category + Brand */}
      <section className="sticky top-[72px] z-40 bg-white border-b border-oya-gray-light py-3 md:py-4 px-4">
        <div className="max-w-7xl mx-auto space-y-2 md:space-y-3">
          {/* Category filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? "bg-oya-black text-white"
                    : "bg-oya-cream text-oya-black hover:bg-oya-gray-light"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Brand filter — only show if brands exist */}
          {availableBrands.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <span className="text-[10px] md:text-xs text-oya-gray-medium font-medium uppercase tracking-wider mr-1 shrink-0">
                Marque
              </span>
              <button
                type="button"
                onClick={() => setActiveBrand("all")}
                className={`px-2.5 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs font-medium whitespace-nowrap transition-all border ${
                  activeBrand === "all"
                    ? "bg-oya-black text-white border-oya-black"
                    : "bg-white text-oya-gray-dark border-oya-gray-light hover:border-oya-gray-medium"
                }`}
              >
                Toutes
              </button>
              {availableBrands.map((brand) => (
                <button
                  key={brand}
                  type="button"
                  onClick={() => setActiveBrand(brand)}
                  className={`px-2.5 md:px-3 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs font-medium whitespace-nowrap transition-all border ${
                    activeBrand === brand
                      ? "bg-oya-black text-white border-oya-black"
                      : "bg-white text-oya-gray-dark border-oya-gray-light hover:border-oya-gray-medium"
                  }`}
                >
                  {brand}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 md:py-20 px-4 bg-white min-h-[400px]">
        <div className="max-w-7xl mx-auto">
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-xl bg-oya-cream h-80" />
              ))}
            </div>
          )}

          {!loading && filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-oya-cream mb-6">
                <i className="ri-shopping-bag-line text-2xl text-oya-gray-medium" />
              </div>
              <p className="text-oya-gray-dark text-lg font-medium">
                Aucun accessoire disponible pour le moment.
              </p>
              <p className="text-oya-gray-medium text-sm mt-2 max-w-sm">
                Les produits sont mis à jour automatiquement depuis notre catalogue. Revenez bientôt ou contactez-nous pour plus d'informations.
              </p>
            </div>
          )}

          {!loading && filteredProducts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Affiliate Mention */}
      <section className="py-8 px-4 bg-oya-cream border-t border-oya-gray-light">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs text-oya-gray-medium">
            Certains liens peuvent être affiliés. Cela ne change pas le prix pour vous.
          </p>
        </div>
      </section>
    </div>
  );
}